import { Box, Grid, GridItem, Toggle, Button, LoadingDots } from '@flos/react-ui';
import { useState } from 'react';
import { useHippoText } from '../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCoverable } from '../../../utils/coverablesUtil';
import { getOnsaCurrentProductSelector } from '../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useCalculateCoveragesPrice } from '../../../recoil/hooks/useCalculateCoveragesPrice';
import { useAddOrRemoveCoverage } from '../../../recoil/hooks/useAddOrRemoveCoverage';
import { useUpdateCoverableInformation } from '../../../recoil/hooks/useUpdateCoverableInformation';
import {
    shouldShowCallMeModal,
    type AdditionalCoverageProps,
    getCoverageFieldToUpdate
} from '../config/AdditionalCoveragesConfig';
import AddAdditionalCoverageConfirmationModal from './AddAdditionalCoverageConfirmationModal';
import AdditionalCoverageInputModal from './additionalCoverageRequiredInputs/AdditionalCoverageInputModal';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import Currency from '../../../shared_components/Currency/Currency';
import type { CoverableOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';

const AdditionalCoverage = ({
    coverage,
    isLoading,
    setIsLoading
}: {
    coverage: AdditionalCoverageProps;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const [showInputModal, setShowInputModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showCallMeModal, setShowCallMeModal] = useState(false);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const { flavourName, fixedId } = useRecoilValue(getOnsaCurrentProductSelector);
    const { addOrRemoveCoverage } = useAddOrRemoveCoverage();
    const { calculateCoveragesPrice } = useCalculateCoveragesPrice();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId);
    const fieldToUpdate = getCoverageFieldToUpdate(coverage.coverageId);
    const coveragePrice = coverable.coverages?.find((cov) => cov.coverageId === coverage.coverageId)
        ?.coveragePrice?.monthlyPrice?.amount;
    const isSelected =
        coverable.coverages?.find((cov) => cov.coverageId === coverage.coverageId) !== undefined;
    const [checked, setChecked] = useState(isSelected);
    const inputRequired = coverage.input !== undefined;
    const getHippoText = useHippoText();

    const handleToggleCoverage = (value: boolean): void => {
        if (inputRequired && !checked) {
            setShowInputModal(value);
        } else if (shouldShowCallMeModal(coverable, coverage)) {
            setShowCallMeModal(true);
        } else if (coverage.shouldShowConfirmationModal && !checked) {
            setShowConfirmationModal(true);
        } else {
            setIsLoading(true);
            void addOrRemoveCoverage(coverage.coverageId, coverable.packageGroupName ?? 'Default')
                .then(calculateCoveragesPrice)
                .then((response) => {
                    updateCoverableInformation({
                        coverages: response.coverages,
                        monthlyPrice: response.coverablePrice.monthlyPrice,
                        yearlyPrice: response.coverablePrice.yearlyPrice
                    });
                    setIsLoading(false);
                });
        }
    };

    return (
        <>
            <Box padding="large" theme={isSelected ? 'highlight' : 'primary'}>
                <Grid variant="right-and-under">
                    <GridItem area="title">
                        <h4 className="u-spacing-stack-none">{coverage.coverageName}</h4>
                    </GridItem>
                    <GridItem area="content">
                        <p className={isSelected ? '' : 'u-spacing-stack-none'}>
                            {getHippoText(coverage?.coverageDescription ?? '')}
                        </p>
                        {isSelected && inputRequired && (
                            <span className="u-flex-box u-flex-box--center u-flex-box--gap-small">
                                <p className="u-spacing-stack-none">
                                    Parameter:{' '}
                                    <Currency
                                        decimals={0}
                                        price={
                                            coverable[
                                                fieldToUpdate as keyof CoverableOnsaDTO
                                            ] as number
                                        }
                                    />
                                </p>
                                <Button
                                    size="small"
                                    theme="secondary"
                                    onClick={() => {
                                        setShowInputModal(true);
                                    }}
                                >
                                    {getHippoText('DSS-COMMON-EDIT')}
                                </Button>
                            </span>
                        )}
                    </GridItem>
                    <GridItem area="right-and-under">
                        <div className="u-flex-box--end">
                            {isLoading && (
                                <div className="u-spacing-stack-top-3 u-spacing-stack-right-8">
                                    <LoadingDots centered={false} />
                                </div>
                            )}
                            {!isLoading && isSelected && (
                                <h4 className="u-spacing-stack-none">
                                    <Currency
                                        decimals={0}
                                        price={coveragePrice ?? 0}
                                        paymentFrequencyPostfix="/md."
                                    />
                                </h4>
                            )}
                            <Toggle
                                checked={checked}
                                onChange={(e) => {
                                    handleToggleCoverage(e.target.checked);
                                    setChecked(e.target.checked);
                                }}
                                disabled={isLoading}
                            />
                        </div>
                    </GridItem>
                </Grid>
            </Box>
            {showInputModal && (
                <AdditionalCoverageInputModal
                    coverage={coverage}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    showModal={showInputModal}
                    setShowModal={setShowInputModal}
                    closeModal={() => {
                        setShowInputModal(false);
                        setChecked(false);
                    }}
                />
            )}
            {showConfirmationModal && (
                <AddAdditionalCoverageConfirmationModal
                    showModal={showConfirmationModal}
                    setShowModal={setShowConfirmationModal}
                    closeModal={() => {
                        setShowConfirmationModal(false);
                        setChecked(false);
                    }}
                    coverage={coverage}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                />
            )}
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_add_coverage_phone_req"
                    showModal={showCallMeModal}
                    title={`${getHippoText('Du kan ikke købe dækningen')} ${coverage?.coverageName?.toLowerCase() ?? ''}`}
                    description={getHippoText(
                        `DSS-ADD-SIMPLE-COVERAGE-${coverage.coverageId.toUpperCase()}-SUMINSURED-CALL-ME`
                    )}
                    closeModal={() => {
                        setShowCallMeModal(false);
                        setChecked(false);
                    }}
                />
            )}
        </>
    );
};

export default AdditionalCoverage;
